/**
 * External Handling — mutation hooks (VALE-841).
 *
 * Handling is read on several surfaces at once (Discovery, Inventory, the records
 * surfaces), so every write invalidates all of them rather than just the one the
 * user was looking at — a badge that disappears on one tab and lingers on another
 * is the failure this avoids.
 */

'use client';

import { UseMutationOptions } from '@tanstack/react-query';
import { createMutationHook } from '../mutation-utils';
import { getDependencies } from '../query-dependencies';
import { discoveryKeys } from '@/lib/query-keys/discovery';
import { inventoryKeys } from '@/lib/query-keys/inventory';
import {
  bulkSetInstitutionHandlingAction,
  setInstitutionHandlingAction,
  setInventoryItemHandlingAction
} from './actions';
import type { Handling, SetHandlingRequest } from './types';

/**
 * Every surface that renders a handling badge. Handling is computed, not written
 * onto items, so a single change can alter what several lists render.
 */
function handlingSurfaceKeys(matterId: number) {
  return [
    ...getDependencies('institution', matterId),
    discoveryKeys.forMatter(matterId),
    inventoryKeys.forMatter(matterId)
  ];
}

/**
 * Phrase the confirmation from the user's point of view, not the endpoint's — the
 * same call both hands work over and takes it back, and "Handling updated" would
 * leave the reader unsure which just happened.
 */
function handlingToastMessage(
  handledByContactMatterId: number | null,
  handlerName?: string
): string {
  if (handledByContactMatterId === null) return 'Taken back into Vale';
  return handlerName ? `Handed over to ${handlerName}` : 'Handed over';
}

export function useSetInstitutionHandling(
  matterId: number,
  options?: UseMutationOptions<
    Handling,
    Error,
    { institutionId: number; handlerName?: string } & SetHandlingRequest
  >
) {
  const useHook = createMutationHook<
    Handling,
    { institutionId: number; handlerName?: string } & SetHandlingRequest
  >({
    mutationFn: async ({
      institutionId,
      handlerName: _handlerName,
      ...data
    }) => {
      const result = await setInstitutionHandlingAction(
        matterId,
        institutionId,
        data
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to update handling');
      }
      return result.data;
    },
    invalidateKeys: handlingSurfaceKeys(matterId),
    refetchKeys: [discoveryKeys.forMatter(matterId)],
    onSuccessMessage: (_data, variables) =>
      handlingToastMessage(
        variables.handledByContactMatterId,
        variables.handlerName
      ),
    onErrorMessage: 'Failed to update handling'
  });

  return useHook(options);
}

/**
 * Bulk set AND bulk revert (EH-002, EH-005) — one hook, because the endpoint and
 * the undo are the same shape. Bulk revert exists so that undoing a bulk hand-over
 * costs one click rather than fifteen.
 */
export function useBulkSetInstitutionHandling(
  matterId: number,
  options?: UseMutationOptions<
    { updated: number },
    Error,
    { institutionIds: number[]; handlerName?: string } & SetHandlingRequest
  >
) {
  const useHook = createMutationHook<
    { updated: number },
    { institutionIds: number[]; handlerName?: string } & SetHandlingRequest
  >({
    mutationFn: async ({
      institutionIds,
      handlerName: _handlerName,
      ...data
    }) => {
      const result = await bulkSetInstitutionHandlingAction(
        matterId,
        institutionIds,
        data
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to update handling');
      }
      return result.data;
    },
    invalidateKeys: handlingSurfaceKeys(matterId),
    refetchKeys: [discoveryKeys.forMatter(matterId)],
    onSuccessMessage: (_data, variables) => {
      const count = variables.institutionIds.length;
      const plural = count === 1 ? '' : 's';
      return variables.handledByContactMatterId === null
        ? `${count} institution${plural} taken back into Vale`
        : `${count} institution${plural} handed over${
            variables.handlerName ? ` to ${variables.handlerName}` : ''
          }`;
    },
    onErrorMessage: 'Failed to update handling'
  });

  return useHook(options);
}

/** Institution-less inventory items only — a house, a car (EH-CR-007). */
export function useSetInventoryItemHandling(
  matterId: number,
  options?: UseMutationOptions<
    Handling,
    Error,
    { inventoryItemId: number; handlerName?: string } & SetHandlingRequest
  >
) {
  const useHook = createMutationHook<
    Handling,
    { inventoryItemId: number; handlerName?: string } & SetHandlingRequest
  >({
    mutationFn: async ({
      inventoryItemId,
      handlerName: _handlerName,
      ...data
    }) => {
      const result = await setInventoryItemHandlingAction(
        matterId,
        inventoryItemId,
        data
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to update handling');
      }
      return result.data;
    },
    invalidateKeys: handlingSurfaceKeys(matterId),
    refetchKeys: [inventoryKeys.forMatter(matterId)],
    onSuccessMessage: (_data, variables) =>
      handlingToastMessage(
        variables.handledByContactMatterId,
        variables.handlerName
      ),
    onErrorMessage: 'Failed to update handling'
  });

  return useHook(options);
}
